import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  BookOpen,
  FileOutput,
  FileSignature,
  FolderOpen,
  Home,
  LogOut,
  Menu,
  MessageSquare,
  ScrollText,
  Settings,
  ShieldCheck,
  Sparkles,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useAuth } from '@/features/auth/auth-provider'
import { useWorkspace } from '@/features/workspace/workspace-provider'
import { supabase } from '@/lib/supabase'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// F03: Drawer bis 760px, Desktop-Sidebar ab 761px. Tailwind md (768px) bleibt
// unberuehrt; der 761px-Umschaltpunkt ist bewusst kein Standard-Breakpoint.
const DESKTOP_QUERY = '(min-width: 761px)'

const PRIMARY_NAV = [
  { to: '/', label: 'Heute', icon: Home },
  { to: '/fragen', label: 'Fragen', icon: MessageSquare },
  { to: '/quellen', label: 'Quellen', icon: FolderOpen },
  { to: '/wissen', label: 'Wissen', icon: BookOpen },
  // Freigaben steht bewusst neben Wissen: der Pruefstapel ist der einzige Ort,
  // an dem ein Entwurf sichtbar ist, und der einzige Weg in eine Antwort.
  { to: '/freigaben', label: 'Freigaben', icon: ShieldCheck },
  // Quellenregister direkt nach den Freigaben: dort steht, woher eine Quelle
  // kommt und welche Lizenzklasse sie traegt — die zweite Frage, die eine
  // pruefende Person hat.
  { to: '/quellenregister', label: 'Quellenregister', icon: ScrollText },
  // Gutachten steht vor den Outputs: es ist der Ausgabetyp, an dem die
  // Zeitersparnis fuer den Piloten sichtbar wird, und der einzige, der
  // deterministisch aus Belegen entsteht statt aus einer LLM-Synthese.
  { to: '/gutachten', label: 'Gutachten', icon: FileSignature },
  { to: '/outputs', label: 'Outputs', icon: FileOutput },
] as const

const SECONDARY_NAV = [
  { to: '/automatisierungen', label: 'Automatisierungen', icon: Sparkles },
  { to: '/einstellungen', label: 'Einstellungen', icon: Settings },
] as const

function NavSection({
  items,
  onNavigate,
}: {
  items: readonly { to: string; label: string; icon: typeof Home }[]
  onNavigate?: () => void
}) {
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/'}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              // F03: aktive Zeile mit Auswahlflaeche (sidebar-accent), zugaenglicher
              // blauer Schrift (sidebar-accent-foreground) und sichtbarem Fokus.
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'min-h-11 min-[761px]:min-h-9',
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          {label}
        </NavLink>
      ))}
    </nav>
  )
}

function AdminNavItem({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <nav className="px-3">
      <NavLink
        to="/admin"
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'min-h-11 min-[761px]:min-h-9',
            isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
          )
        }
      >
        <Settings className="h-4 w-4 shrink-0" />
        Admin
      </NavLink>
    </nav>
  )
}

function MindKeepLogo({ className }: { className?: string }) {
  // F03: reine Wortmarke, kein Signet — kein Flow-/W-Zeichen.
  return (
    <div className={cn('flex items-center', className)}>
      <span className="text-base font-semibold tracking-tight text-foreground">MindKeep</span>
    </div>
  )
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  // M5-B: Admin-Nav nur fuer owner/admin — Rolle kommt aus dem
  // WorkspaceProvider (/workspaces/me). Durchsetzung bleibt serverseitig.
  const { isAdmin } = useWorkspace()
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 shrink-0 items-center border-b px-4">
        <MindKeepLogo />
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        <NavSection items={PRIMARY_NAV} onNavigate={onNavigate} />

        <div className="mx-4 my-3 h-px bg-border" />

        <NavSection items={SECONDARY_NAV} onNavigate={onNavigate} />
      </div>

      {isAdmin ? (
        <div className="shrink-0 border-t py-3">
          <AdminNavItem onNavigate={onNavigate} />
        </div>
      ) : null}
    </div>
  )
}

function WorkspaceSwitcher({
  testId = 'workspace-switcher',
  mobile = false,
}: {
  testId?: string
  mobile?: boolean
}) {
  const { workspace, workspaces, switchWorkspace } = useWorkspace()
  if (workspaces.length <= 1) return null

  return (
    <Select value={workspace.id} onValueChange={switchWorkspace}>
      <SelectTrigger
        data-testid={testId}
        aria-label="Aktiven Workspace wechseln"
        className={cn(
          'min-w-0 border-0 bg-transparent px-2 shadow-none focus:ring-1',
          mobile ? 'h-11 w-full text-left' : 'h-9 w-full max-w-[200px]',
        )}
      >
        {mobile ? (
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold leading-tight">MindKeep</span>
            <span className="block truncate text-xs leading-tight text-muted-foreground" title={workspace.name}>
              <SelectValue placeholder={workspace.name} />
            </span>
          </span>
        ) : (
          <SelectValue placeholder={workspace.name} />
        )}
      </SelectTrigger>
      <SelectContent>
        {workspaces.map((item) => (
          <SelectItem key={item.id} value={item.id}>
            {item.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const { user } = useAuth()
  const { workspace, workspaces, isAdmin } = useWorkspace()
  const navigate = useNavigate()

  // F03: Wechsel auf >=761px schliesst einen offenen mobilen Drawer, damit
  // kein unsichtbarer Overlay den Desktop sperrt.
  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setMobileOpen(false)
    }
    media.addEventListener('change', handleChange)
    return () => media.removeEventListener('change', handleChange)
  }, [])

  const displayName = useMemo(() => {
    const meta = user?.user_metadata as { name?: string; full_name?: string } | undefined
    return meta?.name || meta?.full_name || user?.email?.split('@')[0] || 'Nutzer'
  }, [user])

  const userInitials = useMemo(() => {
    const name = displayName
    if (name.includes(' ')) {
      return name
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
  }, [displayName])

  async function handleLogout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  return (
    // F03: 100dvh mit 100vh-Fallback fuer mobile Browserleisten; kein globales
    // Abschneiden von Ueberlauf — Hauptinhalt scrollt bei geringer Hoehe.
    <div className="flex h-screen supports-[height:100dvh]:h-[100dvh] overflow-hidden bg-background">
      {/* Desktop sidebar — ab 761px, 228px breit */}
      <aside className="hidden min-[761px]:flex min-[761px]:flex-col min-[761px]:w-sidebar min-[761px]:fixed min-[761px]:inset-y-0 border-r bg-sidebar z-30">
        <SidebarContent />
      </aside>

      {/* Main area — Platz fuer die 228px-Sidebar ab 761px */}
      <div className="flex flex-1 flex-col min-w-0 min-[761px]:pl-[var(--fm-sidebar-w)]">
        {/* Topbar / Kontextleiste — 56px */}
        <header className="sticky top-0 z-20 flex h-topbar shrink-0 items-center gap-3 border-b bg-background px-4">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            {/* F03: SheetTrigger asChild — der Menue-Button ist 44px (Mobile-Zielgroesse)
                und Radix gibt Escape den Fokus zurueck. Beschriftung bleibt erhalten. */}
            <SheetTrigger asChild>
              <button
                type="button"
                className="min-[761px]:hidden inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground -ml-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Navigation öffnen"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-sidebar max-w-[85vw] p-0">
              <SheetHeader className="sr-only">
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* F03: kombinierter 44px-Trigger mit zwei Zeilen — Produktname und
              aktiver Workspace. Bei nur einem Workspace wird der Name angezeigt
              (kein Select), bei mehreren bleibt die Auswahl erhalten. Keine
              feste 200px-Breite, die mit Logo, Menue und Avatar auf 360px
              ueberlaeuft. */}
          <div className="flex min-w-0 flex-1 items-center min-[761px]:hidden">
            {workspaces.length > 1 ? (
              <WorkspaceSwitcher testId="workspace-switcher-mobile" mobile />
            ) : (
              <div className="flex h-11 min-w-0 flex-col justify-center leading-tight">
                <MindKeepLogo className="text-sm" />
                <div className="min-w-0 truncate text-xs text-muted-foreground" title={workspace.name}>
                  {workspace.name}
                </div>
              </div>
            )}
          </div>

          <div className="hidden min-[761px]:flex items-center gap-2 min-w-0 text-sm text-muted-foreground">
            {workspaces.length > 1 ? (
              <WorkspaceSwitcher />
            ) : (
              <>
                <span className="font-medium text-foreground truncate">{workspace.name}</span>
                <span className="text-border">/</span>
                <span>Mein MindKeep</span>
              </>
            )}
          </div>

          <div className="flex-1" />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="h-11 min-[761px]:h-9 gap-2 rounded-md px-2 text-sm font-normal focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Benutzermenu"
              >
                <Avatar className="h-7 w-7">
                  <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-[10px] font-semibold">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline max-w-[140px] truncate">{displayName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="font-normal">
                <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <NavLink to="/einstellungen/profil" className="flex w-full cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  Profil
                </NavLink>
              </DropdownMenuItem>
              {isAdmin ? (
                <DropdownMenuItem asChild>
                  <NavLink to="/admin" className="flex w-full cursor-pointer">
                    <Settings className="mr-2 h-4 w-4" />
                    Admin
                  </NavLink>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive cursor-pointer"
                onSelect={handleLogout}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Abmelden
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
